import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { Project } from '@/hooks/useProjects'
import { ImageWithFallback } from './figma/ImageWithFallback'
import { useTranslation } from '@/hooks/useTypedTranslation'

interface ProjectCardProps {
  project: Project
}

export function ProjectCard({ project }: ProjectCardProps) {
  const { t } = useTranslation()
  
  const statusStyles = {
    completed: {
      bg: 'bg-accent-emerald/10',
      text: 'text-accent-emerald',
      label: t.status.completed
    },
    'in-progress': {
      bg: 'bg-accent-blue/10',
      text: 'text-accent-blue',
      label: t.status.inProgress
    },
    planned: {
      bg: 'bg-accent-purple/10',
      text: 'text-accent-purple',
      label: t.status.planned
    }
  }

  const status = statusStyles[project.status]

  return (
    <Link 
      to={`/${project.category_id}/${project.id}`}
      className="group block"
    >
      <div className="bg-card border border-border rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-primary/20 hover:-translate-y-1">
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <ImageWithFallback
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          
          {/* Status Badge */}
          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
              {status.label}
            </span>
          </div>

          {/* External Link Icon */}
          {project.link && (
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                <ExternalLink className="w-4 h-4 text-foreground" />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
            {project.title}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">
            {project.description}
          </p>
        </div>
      </div>
    </Link>
  )
}
